import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { MarginTrend } from "../types"
import { formatAxisMnt, formatMnt } from "../utils"
import { AnalyticsCard } from "./AnalyticsCard"
import { AXIS_LABEL, AXIS_LINE, GRID_LINE, LEGEND, SERIES, TOOLTIP, YoYToggle } from "./chart-theme"

type Props = {
  data: MarginTrend | undefined
  isLoading: boolean
  compareYoY: boolean
  onToggleYoY: () => void
  currentYear: number
}

export function MarginTrendChart({ data, isLoading, compareYoY, onToggleYoY, currentYear }: Props) {
  const { t } = useTranslation(["dashboard"])

  const options = useMemo<Highcharts.Options>(() => {
    const labels = data?.data.current.map(b => b.label) ?? []
    const currentMargin = data?.data.current.map(b => b.marginAmount) ?? []
    const currentPct    = data?.data.current.map(b => b.marginPct) ?? []
    const prevMargin    = data?.data.previousYear?.map(b => b.marginAmount) ?? []

    const series: Highcharts.SeriesOptionsType[] = [
      {
        type        : "column",
        name        : `${t("analytics.margin.amount", { defaultValue: "Маржин ₮" })} (${currentYear})`,
        data        : currentMargin,
        color       : SERIES.current,
        borderRadius: 2,
        yAxis       : 0
      }
    ]
    if (compareYoY && prevMargin.length > 0) {
      series.push({
        type        : "column",
        name        : `${t("analytics.margin.amount", { defaultValue: "Маржин ₮" })} (${currentYear - 1})`,
        data        : prevMargin,
        color       : SERIES.previous,
        borderRadius: 2,
        yAxis       : 0
      })
    }
    series.push({
      type     : "line",
      name     : t("analytics.margin.pct", { defaultValue: "Маржин %" }),
      data     : currentPct,
      color    : SERIES.accent,
      lineWidth: 2,
      marker   : { enabled: true, radius: 3, symbol: "circle" },
      yAxis    : 1,
      zIndex   : 5
    })

    return {
      chart  : { type: "column", backgroundColor: "transparent", height: 260, style: { fontFamily: "inherit" } },
      title  : { text: undefined },
      credits: { enabled: false },
      accessibility: { enabled: false },
      legend : LEGEND,
      xAxis  : { categories: labels, gridLineWidth: 0, labels: { style: AXIS_LABEL }, lineColor: AXIS_LINE },
      yAxis  : [
        { title: { text: undefined }, gridLineColor: GRID_LINE, labels: { style: AXIS_LABEL, formatter() { return formatAxisMnt(this.value as number) } } },
        { opposite: true, title: { text: undefined }, gridLineWidth: 0, min: 0, labels: { style: AXIS_LABEL, formatter() { return `${this.value}%` } } }
      ],
      tooltip: {
        ...TOOLTIP,
        formatter() {
          const points = this.points ?? []
          const body = points
            .map(p => {
              const v = p.y
              if (v === null || v === undefined) return `${p.series.name}: —`
              const shown = p.series.userOptions.yAxis === 1 ? `${(v as number).toFixed(1)}%` : formatMnt(v as number)
              return `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${shown}</b>`
            }).join("<br/>")
          return `<b>${this.x}</b><br/>${body}`
        }
      },
      plotOptions: { column: { borderWidth: 0, pointPadding: 0.1, groupPadding: 0.15, maxPointWidth: 28 } },
      series
    }
  }, [data, compareYoY, currentYear, t])

  return (
    <AnalyticsCard
      title={t("analytics.margin.trendTitle", { defaultValue: "Маржин трэнд" })}
      subtitle={t("analytics.margin.trendSubtitle", { defaultValue: "Нийт маржин ₮ + Маржин % · Сараар" })}
      isLoading={isLoading}
      right={<YoYToggle active={compareYoY} onToggle={onToggleYoY} />}
    >
      <HighchartsReact highcharts={Highcharts} options={options} />
    </AnalyticsCard>
  )
}
